import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { EMPTY, Subject } from 'rxjs';

import { PrestadorDocumentosOpcaoResponse, PrestadorDocumentosReferenciasResponse, PrestadorDocumentosRequest, PrestadorDocumentosResponse } from '@/core/api/sicom-api/registro/prestador/prestador-documentos.model';
import { PrestadorDocumentosService } from '@/core/api/sicom-api/registro/prestador/prestador-documentos.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { FormDatePicker } from '@/shared/components/layout/form-date-picker/form-date-picker';
import { DateUtils } from '@/shared/utils/date-utils';
import { ValueUtils } from '@/shared/utils/value-utils';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';

type OpcaoSelect = {
    label: string;
    value: string;
    group?: string | null;
    parent?: string | null;
};

@Component({
    selector: 'app-documentos-prestador',
    standalone: true,
    imports: [ReactiveFormsModule, CardContent, InputText, InputMask, Select, BotaoAcoes, FormDatePicker],
    templateUrl: './documentos-prestador.html',
    styleUrl: './documentos-prestador.scss'
})
export class DocumentosPrestador {
    pessoa = input<Pessoa>();
    protected readonly rolePrestadoresDocumentosEditar = PERMISSOES.prestadores.documentos.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        const role = this.rolePrestadoresDocumentosEditar;
        return [acaoMenuItem({ id: 'editar', label: 'Editar', icon: 'pi pi-pencil', cor: 'editar', disabled: this.isEditing, permission: role, command: () => this.enableEditing() })];
    }

    /** Ver {@link itensAcoes}: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.rolePrestadoresDocumentosEditar;
        return [
            acaoMenuItem({
                id: 'salvar',
                label: 'Salvar',
                icon: 'pi pi-check',
                cor: 'salvar',
                disabled: !this.form.valid || this.carregando(),
                permission: role,
                command: () => this.saveChanges()
            }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelEditing() })
        ];
    }

    private readonly destroyRef = inject(DestroyRef);
    private readonly camposEditaveis = [
        'inInstituido',
        'nuRegistroSanitario',
        'inNatRegistroSanitario',
        'inValRegSanitario',
        'dtValRegistroSanitario',
        'nuLicFuncionamento',
        'inValLicFuncionamento',
        'dtValLicFuncionamento',
        'inLocalRegistro',
        'idDocumentoConstituicao',
        'nuLivroDocConstituicao',
        'nuFolhaDocConstituicao',
        'nuDocumentoConstituicao',
        'dtDocumentoConstituicao',
        'nuCnes',
        'inEnsino',
        'inGestao',
        'inGestaoTipo',
        'inAbrangenciaServico',
        'inCondicaoFuncionamento',
        'inHomologacaoRi',
        'dtHomologacaoRi',
        'idPeriodoMandato',
        'idNaturezaEmpresa'
    ] as const;

    private readonly fb = inject(FormBuilder);
    readonly form = this.fb.group({
        cnpj: [{ value: '', disabled: true }],
        inInstituido: this.fb.control<string | null>({ value: null, disabled: true }),
        nuRegistroSanitario: [{ value: '', disabled: true }, Validators.maxLength(30)],
        inNatRegistroSanitario: this.fb.control<string | null>({ value: null, disabled: true }),
        inValRegSanitario: [{ value: 'D', disabled: true }],
        dtValRegistroSanitario: this.fb.control<Date | null>({ value: null, disabled: true }),
        nuLicFuncionamento: [{ value: '', disabled: true }, Validators.maxLength(15)],
        inValLicFuncionamento: [{ value: 'D', disabled: true }],
        dtValLicFuncionamento: this.fb.control<Date | null>({ value: null, disabled: true }),
        inLocalRegistro: [{ value: 'C', disabled: true }],
        idDocumentoConstituicao: this.fb.control<string | null>({ value: null, disabled: true }),
        nuLivroDocConstituicao: [{ value: '', disabled: true }, Validators.maxLength(5)],
        nuFolhaDocConstituicao: [{ value: '', disabled: true }, Validators.maxLength(10)],
        nuDocumentoConstituicao: [{ value: '', disabled: true }, Validators.maxLength(30)],
        dtDocumentoConstituicao: this.fb.control<Date | null>({ value: null, disabled: true }),
        dtAtualDocConstituicao: this.fb.control<Date | null>({ value: null, disabled: true }),
        nuCnes: [{ value: '', disabled: true }, Validators.pattern(/^d*$/)],
        inEnsino: this.fb.control<string | null>({ value: null, disabled: true }),
        inGestao: this.fb.control<string | null>({ value: null, disabled: true }),
        inGestaoTipo: this.fb.control<string | null>({ value: null, disabled: true }),
        inAbrangenciaServico: this.fb.control<string | null>({ value: null, disabled: true }),
        inCondicaoFuncionamento: this.fb.control<string | null>({ value: null, disabled: true }),
        inHomologacaoRi: this.fb.control<string | null>({ value: null, disabled: true }),
        dtHomologacaoRi: this.fb.control<Date | null>({ value: null, disabled: true }),
        idPeriodoMandato: this.fb.control<string | null>({ value: null, disabled: true }),
        idNaturezaEmpresa: this.fb.control<string | null>({ value: null, disabled: true })
    });
    readonly carregando = signal(false);
    isEditing = false;

    opcoesInstituido: OpcaoSelect[] = [];
    opcoesNaturezaRegistroSanitario: OpcaoSelect[] = [];
    opcoesPrazoIndeterminado: OpcaoSelect[] = [];
    opcoesLocalRegistro: OpcaoSelect[] = [];
    opcoesEnsino: OpcaoSelect[] = [];
    opcoesGestao: OpcaoSelect[] = [];
    opcoesTipoGestao: OpcaoSelect[] = [];
    opcoesAbrangencia: OpcaoSelect[] = [];
    opcoesCondicaoFuncionamento: OpcaoSelect[] = [];
    opcoesRegimentoInterno: OpcaoSelect[] = [];
    opcoesPeriodoMandatoCem: OpcaoSelect[] = [];
    opcoesTipoDocumento: OpcaoSelect[] = [];
    opcoesNaturezaEmpresa: OpcaoSelect[] = [];

    numeroDocumentoLabel = 'Nº do Documento de Constituição';
    dadosCarregados: PrestadorDocumentosResponse | null = null;

    /** Pedidos de recarga dos documentos. Ver a assinatura no construtor. */
    private readonly recarregar$ = new Subject<Pessoa>();

    constructor(
        private readonly prestadorDocumentosService: PrestadorDocumentosService,
        private readonly notificacoesService: NotificacoesService
    ) {
        this.configurarRegrasFormulario();

        // Recarga como fluxo: o `switchMap` descarta a busca anterior quando chega um pedido novo,
        // que e a semantica de "recarrega o estado atual". O guard de reentrancia que existia aqui
        // fazia o oposto — descartava o pedido NOVO —, entao trocar de prestador durante uma carga
        // deixava a tela com os documentos do prestador anterior.
        this.recarregar$
            .pipe(
                switchMap((pessoa) => {
                    // `set(true)` fica aqui dentro: o switchMap desinscreve o inner anterior antes
                    // de projetar, e o `finalize` dele zeraria o sinal logo depois de um `tap`.
                    this.carregando.set(true);
                    return this.prestadorDocumentosService.buscar(pessoa).pipe(
                        finalize(() => this.carregando.set(false)),
                        // `catchError` no fluxo interno: no externo, o primeiro erro encerraria a
                        // assinatura e nenhuma recarga posterior voltaria a funcionar.
                        catchError(() => {
                            this.limparFormulario();
                            return EMPTY;
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((response) => {
                this.aplicarDados(response);
                this.isEditing = false;
            });

        effect(() => {
            const pessoa = this.pessoa();
            if (!pessoa?.id || !pessoa?.uf) {
                this.limparFormulario();
                return;
            }

            untracked(() => this.carregarDados(pessoa));
        });
    }

    get isJuntaComercial(): boolean {
        return this.form.get('inLocalRegistro')?.value === 'J';
    }

    get exibirValidadeRegistroSanitario(): boolean {
        return this.form.get('inValRegSanitario')?.value === 'D';
    }

    get exibirValidadeAlvara(): boolean {
        return this.form.get('inValLicFuncionamento')?.value === 'D';
    }

    /**
     * Legado (UntPrestador.pas, SalvarDocumentos): com Regimento Interno homologado ('S') e sem
     * data de homologação ainda gravada, o operador é obrigado a informar o período de mandato
     * da CEM (form TFrmMandatoCEM) antes de salvar.
     */
    get exigePeriodoMandatoCem(): boolean {
        return this.form.get('inHomologacaoRi')?.value === 'S' && !this.form.get('dtHomologacaoRi')?.value;
    }

    /**
     * Legado (UntCadPrestador.pas:2403/3244): quando a classificação do prestador é Sede Administrativa
     * (IN_SEDE_ADMINISTRATIVA = 'S'), o campo CNES fica desabilitado (DBEdtCNES.Enabled := not ClassificacaoSedeAdministrativa).
     */
    get isSedeAdministrativa(): boolean {
        return this.dadosCarregados?.sedeAdministrativa === true;
    }

    enableEditing(): void {
        this.isEditing = true;
        this.camposEditaveis.forEach((campo) => this.form.get(campo)?.enable({ emitEvent: false }));
        this.sincronizarCamposPorPrazoRegistro();
        this.sincronizarCamposPorPrazoAlvara();
        this.sincronizarCamposPorLocalRegistro();
        this.sincronizarCamposPorGestao();
        this.sincronizarCamposPorHomologacao();
        this.sincronizarCampoCnes();
    }

    saveChanges(): void {
        if (this.form.invalid || !this.pessoa()) {
            this.form.markAllAsTouched();

            if (this.exigePeriodoMandatoCem && this.form.get('idPeriodoMandato')?.invalid) {
                this.notificacoesService.notificar({
                    severity: 'warn',
                    summary: 'Período de mandato CEM obrigatório',
                    detail: 'Informe o período de mandato da CEM para homologar o regimento interno.'
                });
            }

            return;
        }

        const request = this.construirRequest();
        if (!request) {
            return;
        }

        this.carregando.set(true);
        this.prestadorDocumentosService
            .atualizar(request)
            .pipe(
                finalize(() => this.carregando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (response) => {
                    this.aplicarDados(response);
                    this.isEditing = false;
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Documentos salvos',
                        detail: 'Documentos do prestador atualizados com sucesso.'
                    });
                },
                // Vazio de proposito: o toast e do interceptor global e o rascunho fica em edicao para o usuario corrigir.
                error: () => {}
            });
    }

    cancelEditing(): void {
        if (this.dadosCarregados) {
            this.aplicarDados(this.dadosCarregados);
        } else {
            this.limparFormulario();
        }
        this.isEditing = false;
    }

    private configurarRegrasFormulario(): void {
        this.form
            .get('inValRegSanitario')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorPrazoRegistro());
        this.form
            .get('inValLicFuncionamento')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorPrazoAlvara());
        this.form
            .get('inLocalRegistro')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorLocalRegistro());
        this.form
            .get('inGestao')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorGestao());
        this.form
            .get('inHomologacaoRi')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorHomologacao());
        this.form
            .get('dtHomologacaoRi')
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.sincronizarCamposPorHomologacao());
    }

    /** Pede a recarga. Sem guard: quem chama de dentro de outro subscribe e atendido igual. */
    private carregarDados(pessoa: Pessoa): void {
        this.recarregar$.next(pessoa);
    }

    private aplicarDados(response: PrestadorDocumentosResponse): void {
        this.dadosCarregados = response;
        this.definirOpcoes(response.referencias);

        this.form.patchValue(
            {
                cnpj: response.cnpj ?? '',
                inInstituido: ValueUtils.toSelectValue(response.inInstituido),
                nuRegistroSanitario: response.nuRegistroSanitario ?? '',
                inNatRegistroSanitario: ValueUtils.toSelectValue(response.inNatRegistroSanitario),
                inValRegSanitario: ValueUtils.toSelectValue(response.inValRegSanitario) ?? 'D',
                dtValRegistroSanitario: DateUtils.parseToDate(response.dtValRegistroSanitario),
                nuLicFuncionamento: response.nuLicFuncionamento ?? '',
                inValLicFuncionamento: ValueUtils.toSelectValue(response.inValLicFuncionamento) ?? 'D',
                dtValLicFuncionamento: DateUtils.parseToDate(response.dtValLicFuncionamento),
                inLocalRegistro: ValueUtils.toSelectValue(response.inLocalRegistro) ?? 'C',
                idDocumentoConstituicao: ValueUtils.toSelectValue(response.idDocumentoConstituicao),
                nuLivroDocConstituicao: response.nuLivroDocConstituicao ?? '',
                nuFolhaDocConstituicao: response.nuFolhaDocConstituicao ?? '',
                nuDocumentoConstituicao: response.nuDocumentoConstituicao ?? '',
                dtDocumentoConstituicao: DateUtils.parseToDate(response.dtDocumentoConstituicao),
                dtAtualDocConstituicao: DateUtils.parseToDate(response.dtAtualDocConstituicao),
                nuCnes: response.nuCnes ? String(response.nuCnes) : '',
                inEnsino: ValueUtils.toSelectValue(response.inEnsino),
                inGestao: ValueUtils.toSelectValue(response.inGestao),
                inGestaoTipo: ValueUtils.toSelectValue(response.inGestaoTipo),
                inAbrangenciaServico: ValueUtils.toSelectValue(response.inAbrangenciaServico),
                inCondicaoFuncionamento: ValueUtils.toSelectValue(response.inCondicaoFuncionamento),
                inHomologacaoRi: ValueUtils.toSelectValue(response.inHomologacaoRi),
                dtHomologacaoRi: DateUtils.parseToDate(response.dtHomologacaoRi),
                idPeriodoMandato: ValueUtils.toSelectValue(response.idPeriodoMandato),
                idNaturezaEmpresa: ValueUtils.toSelectValue(response.idNaturezaEmpresa)
            },
            { emitEvent: false }
        );

        this.sincronizarCamposPorPrazoRegistro();
        this.sincronizarCamposPorPrazoAlvara();
        this.sincronizarCamposPorLocalRegistro();
        this.sincronizarCamposPorGestao();
        this.sincronizarCamposPorHomologacao();
        this.sincronizarCampoCnes();
        this.form.disable({ emitEvent: false });
    }

    private limparFormulario(): void {
        this.dadosCarregados = null;
        this.form.reset(
            {
                cnpj: '',
                inInstituido: null,
                nuRegistroSanitario: '',
                inNatRegistroSanitario: null,
                inValRegSanitario: 'D',
                dtValRegistroSanitario: null,
                nuLicFuncionamento: '',
                inValLicFuncionamento: 'D',
                dtValLicFuncionamento: null,
                inLocalRegistro: 'C',
                idDocumentoConstituicao: null,
                nuLivroDocConstituicao: '',
                nuFolhaDocConstituicao: '',
                nuDocumentoConstituicao: '',
                dtDocumentoConstituicao: null,
                dtAtualDocConstituicao: null,
                nuCnes: '',
                inEnsino: null,
                inGestao: null,
                inGestaoTipo: null,
                inAbrangenciaServico: null,
                inCondicaoFuncionamento: null,
                inHomologacaoRi: null,
                dtHomologacaoRi: null,
                idPeriodoMandato: null,
                idNaturezaEmpresa: null
            },
            { emitEvent: false }
        );
        this.numeroDocumentoLabel = 'Nº do Documento de Constituição';
        this.form.disable({ emitEvent: false });
    }

    private definirOpcoes(referencias?: PrestadorDocumentosReferenciasResponse | null): void {
        this.opcoesInstituido = this.mapearOpcoes(referencias?.instituido);
        this.opcoesNaturezaRegistroSanitario = this.mapearOpcoes(referencias?.naturezaRegistroSanitario);
        this.opcoesPrazoIndeterminado = this.mapearOpcoes(referencias?.prazoIndeterminado);
        this.opcoesLocalRegistro = this.mapearOpcoes(referencias?.localRegistro);
        this.opcoesEnsino = this.mapearOpcoes(referencias?.ensino);
        this.opcoesGestao = this.mapearOpcoes(referencias?.gestao);
        this.opcoesTipoGestao = this.mapearOpcoes(referencias?.tipoGestao);
        this.opcoesAbrangencia = this.mapearOpcoes(referencias?.abrangenciaServico);
        this.opcoesCondicaoFuncionamento = this.mapearOpcoes(referencias?.condicaoFuncionamento);
        this.opcoesRegimentoInterno = this.mapearOpcoes(referencias?.homologacaoRi);
        this.opcoesPeriodoMandatoCem = this.mapearOpcoes(referencias?.periodoMandatoCem);
        this.opcoesTipoDocumento = this.mapearOpcoes(referencias?.tiposDocumentoConstituicao);
        this.opcoesNaturezaEmpresa = this.mapearOpcoes(referencias?.naturezasEmpresa);
    }

    private mapearOpcoes(opcoes?: PrestadorDocumentosOpcaoResponse[] | null): OpcaoSelect[] {
        return (opcoes ?? []).map((item) => ({
            label: item.descricao,
            value: item.valor,
            group: item.grupo,
            parent: item.pai
        }));
    }

    private sincronizarCamposPorPrazoRegistro(): void {
        const control = this.form.get('dtValRegistroSanitario');
        if (!this.isEditing) {
            return;
        }
        if (this.form.get('inValRegSanitario')?.value === 'D') {
            control?.enable({ emitEvent: false });
            return;
        }
        control?.setValue(null, { emitEvent: false });
        control?.disable({ emitEvent: false });
    }

    private sincronizarCamposPorPrazoAlvara(): void {
        const control = this.form.get('dtValLicFuncionamento');
        if (!this.isEditing) {
            return;
        }
        if (this.form.get('inValLicFuncionamento')?.value === 'D') {
            control?.enable({ emitEvent: false });
            return;
        }
        control?.setValue(null, { emitEvent: false });
        control?.disable({ emitEvent: false });
    }

    private sincronizarCamposPorLocalRegistro(): void {
        const isJuntaComercial = this.isJuntaComercial;
        this.numeroDocumentoLabel = isJuntaComercial ? 'Número Nire' : 'Nº do Documento de Constituição';

        const tipoDocumento = this.form.get('idDocumentoConstituicao');
        const livro = this.form.get('nuLivroDocConstituicao');
        const folha = this.form.get('nuFolhaDocConstituicao');

        if (!this.isEditing) {
            return;
        }

        if (isJuntaComercial) {
            tipoDocumento?.setValue(null, { emitEvent: false });
            livro?.setValue('', { emitEvent: false });
            folha?.setValue('', { emitEvent: false });
            this.form.get('nuDocumentoConstituicao')?.setValue('', { emitEvent: false });
            this.form.get('dtDocumentoConstituicao')?.setValue(null, { emitEvent: false });
            this.form.get('dtAtualDocConstituicao')?.setValue(null, { emitEvent: false });
            tipoDocumento?.disable({ emitEvent: false });
            livro?.disable({ emitEvent: false });
            folha?.disable({ emitEvent: false });
            return;
        }

        tipoDocumento?.enable({ emitEvent: false });
        livro?.enable({ emitEvent: false });
        folha?.enable({ emitEvent: false });
    }

    private sincronizarCampoCnes(): void {
        const cnes = this.form.get('nuCnes');
        if (!this.isEditing) {
            return;
        }
        if (this.isSedeAdministrativa) {
            cnes?.disable({ emitEvent: false });
            return;
        }
        cnes?.enable({ emitEvent: false });
    }

    private sincronizarCamposPorGestao(): void {
        const tipoGestao = this.form.get('inGestaoTipo');
        if (!this.isEditing) {
            return;
        }
        if (this.form.get('inGestao')?.value === 'S') {
            tipoGestao?.enable({ emitEvent: false });
            return;
        }
        tipoGestao?.setValue(null, { emitEvent: false });
        tipoGestao?.disable({ emitEvent: false });
    }

    private sincronizarCamposPorHomologacao(): void {
        const data = this.form.get('dtHomologacaoRi');
        const periodoMandato = this.form.get('idPeriodoMandato');
        if (!this.isEditing) {
            return;
        }

        if (this.form.get('inHomologacaoRi')?.value === 'S') {
            data?.enable({ emitEvent: false });
            periodoMandato?.enable({ emitEvent: false });
            periodoMandato?.setValidators(this.exigePeriodoMandatoCem ? Validators.required : null);
            periodoMandato?.updateValueAndValidity({ emitEvent: false });
            return;
        }

        data?.setValue(null, { emitEvent: false });
        data?.disable({ emitEvent: false });
        periodoMandato?.setValue(null, { emitEvent: false });
        periodoMandato?.setValidators(null);
        periodoMandato?.disable({ emitEvent: false });
    }

    private construirRequest(): PrestadorDocumentosRequest | null {
        const pessoa = this.pessoa();
        if (!pessoa) {
            return null;
        }

        const raw = this.form.getRawValue();
        return {
            id: pessoa.id,
            uf: pessoa.uf,
            inInstituido: ValueUtils.normalizeText(raw.inInstituido),
            nuRegistroSanitario: ValueUtils.normalizeText(raw.nuRegistroSanitario),
            inNatRegistroSanitario: ValueUtils.normalizeText(raw.inNatRegistroSanitario),
            inValRegSanitario: ValueUtils.normalizeText(raw.inValRegSanitario) ?? 'D',
            dtValRegistroSanitario: raw.inValRegSanitario === 'D' ? DateUtils.formatDateToApi(raw.dtValRegistroSanitario) : null,
            nuLicFuncionamento: ValueUtils.normalizeText(raw.nuLicFuncionamento),
            inValLicFuncionamento: ValueUtils.normalizeText(raw.inValLicFuncionamento) ?? 'D',
            dtValLicFuncionamento: raw.inValLicFuncionamento === 'D' ? DateUtils.formatDateToApi(raw.dtValLicFuncionamento) : null,
            inLocalRegistro: ValueUtils.normalizeText(raw.inLocalRegistro),
            idDocumentoConstituicao: this.isJuntaComercial ? null : ValueUtils.toNumber(raw.idDocumentoConstituicao),
            nuLivroDocConstituicao: this.isJuntaComercial ? null : ValueUtils.normalizeText(raw.nuLivroDocConstituicao),
            nuFolhaDocConstituicao: this.isJuntaComercial ? null : ValueUtils.normalizeText(raw.nuFolhaDocConstituicao),
            nuDocumentoConstituicao: this.isJuntaComercial ? null : ValueUtils.normalizeText(raw.nuDocumentoConstituicao),
            dtDocumentoConstituicao: this.isJuntaComercial ? null : DateUtils.formatDateToApi(raw.dtDocumentoConstituicao),
            nuCnes: ValueUtils.toNumber(raw.nuCnes),
            inEnsino: ValueUtils.normalizeText(raw.inEnsino),
            inGestao: ValueUtils.normalizeText(raw.inGestao),
            inGestaoTipo: raw.inGestao === 'S' ? ValueUtils.normalizeText(raw.inGestaoTipo) : null,
            inAbrangenciaServico: ValueUtils.normalizeText(raw.inAbrangenciaServico),
            inCondicaoFuncionamento: ValueUtils.normalizeText(raw.inCondicaoFuncionamento),
            inHomologacaoRi: ValueUtils.normalizeText(raw.inHomologacaoRi),
            dtHomologacaoRi: raw.inHomologacaoRi === 'S' ? DateUtils.formatDateToApi(raw.dtHomologacaoRi) : null,
            idPeriodoMandato: raw.inHomologacaoRi === 'S' ? ValueUtils.toNumber(raw.idPeriodoMandato) : null,
            idNaturezaEmpresa: ValueUtils.toNumber(raw.idNaturezaEmpresa)
        };
    }
}
